import { useState, useMemo } from 'react';
import { X, MagnifyingGlass } from '@phosphor-icons/react';
import * as PhosphorIcons from '@phosphor-icons/react';

// Curated list of icons useful for business/marketing pages
const ICON_CATEGORIES = {
  'Beliebt': [
    'Star', 'Heart', 'Check', 'CheckCircle', 'Lightning', 'Fire', 'Sparkle',
    'Trophy', 'Medal', 'Crown', 'Diamond', 'Gift', 'Rocket', 'Target',
  ],
  'Kommunikation': [
    'Chat', 'ChatCircle', 'ChatText', 'Envelope', 'EnvelopeSimple', 'Phone',
    'PhoneCall', 'WhatsappLogo', 'Megaphone', 'Bell', 'At', 'PaperPlaneTilt',
  ],
  'Business': [
    'Briefcase', 'Buildings', 'ChartLine', 'ChartBar', 'TrendUp', 'CurrencyEur',
    'CurrencyDollar', 'Wallet', 'Bank', 'Handshake', 'UsersThree', 'UserCircle',
  ],
  'Gesundheit & Wellness': [
    'Heart', 'Heartbeat', 'FirstAid', 'Pill', 'Drop', 'Leaf', 'Plant', 'Tree',
    'Sun', 'Moon', 'Brain', 'Barbell', 'PersonSimpleRun', 'Yoga',
  ],
  'Produkte & Shopping': [
    'ShoppingCart', 'ShoppingBag', 'Storefront', 'Tag', 'Percent', 'Package',
    'Truck', 'Basket', 'Receipt', 'CreditCard', 'Barcode', 'Gift',
  ],
  'Zeit & Planung': [
    'Calendar', 'CalendarCheck', 'Clock', 'Timer', 'Hourglass', 'Alarm',
    'CalendarBlank', 'ListChecks', 'ListBullets', 'Clipboard', 'Note',
  ],
  'Navigation': [
    'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'CaretRight', 'CaretDown',
    'MapPin', 'Compass', 'NavigationArrow', 'SignIn', 'SignOut', 'House',
  ],
  'Medien': [
    'Camera', 'Image', 'Video', 'Play', 'PlayCircle', 'Microphone', 'Music',
    'FilmStrip', 'Television', 'Monitor', 'DeviceMobile', 'Laptop',
  ],
  'Sicherheit & Vertrauen': [
    'Shield', 'ShieldCheck', 'Lock', 'LockOpen', 'Key', 'Eye', 'EyeSlash',
    'Fingerprint', 'Certificate', 'Seal', 'SealCheck', 'Stamp',
  ],
  'Natur & Umwelt': [
    'Leaf', 'Plant', 'Tree', 'Flower', 'Sun', 'Moon', 'Cloud', 'Drop',
    'Mountains', 'Wave', 'Wind', 'Snowflake', 'Rainbow', 'Globe',
  ],
  'Emotionen': [
    'Smiley', 'SmileyWink', 'Heart', 'ThumbsUp', 'ThumbsDown', 'HandsClapping',
    'Confetti', 'Balloon', 'Party', 'Star', 'Fire', 'Lightning',
  ],
  'Tools & Einstellungen': [
    'Gear', 'Wrench', 'Hammer', 'PaintBrush', 'Pencil', 'Scissors', 'Ruler',
    'Sliders', 'FadersHorizontal', 'Funnel', 'MagicWand', 'Lightbulb',
  ],
};

// Flatten all icons for search
const ALL_ICONS = Object.values(ICON_CATEGORIES).flat();
const UNIQUE_ICONS = [...new Set(ALL_ICONS)];

interface IconPickerProps {
  value: string | null;
  onChange: (iconName: string) => void;
  onClose: () => void;
}

export function IconPicker({ value, onChange, onClose }: IconPickerProps) {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredIcons = useMemo(() => {
    if (search) {
      return UNIQUE_ICONS.filter((name) =>
        name.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (selectedCategory) {
      return ICON_CATEGORIES[selectedCategory as keyof typeof ICON_CATEGORIES] || [];
    }
    return UNIQUE_ICONS;
  }, [search, selectedCategory]);

  const renderIcon = (iconName: string) => {
    const IconComponent = (PhosphorIcons as unknown as Record<string, React.ComponentType<{ size?: number; weight?: string; className?: string }>>)[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={24} weight="regular" />;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Icon auswählen</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Icon suchen..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedCategory(null);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Categories */}
        {!search && (
          <div className="px-5 py-3 border-b border-gray-100 flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                !selectedCategory
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Alle
            </button>
            {Object.keys(ICON_CATEGORIES).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Icons Grid */}
        <div className="flex-1 overflow-auto p-5">
          <div className="grid grid-cols-8 gap-2">
            {filteredIcons.map((iconName) => {
              const isSelected = value === iconName;
              return (
                <button
                  key={iconName}
                  onClick={() => {
                    onChange(iconName);
                    onClose();
                  }}
                  title={iconName}
                  className={`aspect-square flex items-center justify-center rounded-xl transition-all ${
                    isSelected
                      ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-500'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {renderIcon(iconName)}
                </button>
              );
            })}
          </div>

          {filteredIcons.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Keine Icons gefunden für "{search}"
            </div>
          )}
        </div>

        {/* Footer */}
        {value && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-gray-200">
                {renderIcon(value)}
              </div>
              <span className="text-sm text-gray-600">
                Ausgewählt: <span className="font-medium text-gray-900">{value}</span>
              </span>
            </div>
            <button
              onClick={() => {
                onChange('');
                onClose();
              }}
              className="text-sm text-red-500 hover:text-red-600"
            >
              Entfernen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Small button component to trigger the icon picker
interface IconPickerButtonProps {
  value: string | null;
  onChange: (iconName: string) => void;
  placeholder?: string;
}

export function IconPickerButton({ value, onChange, placeholder = 'Icon wählen' }: IconPickerButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const renderIcon = (iconName: string) => {
    const IconComponent = (PhosphorIcons as unknown as Record<string, React.ComponentType<{ size?: number; weight?: string }>>)[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={20} weight="regular" />;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${
          value ? 'text-gray-900' : 'text-gray-400'
        }`}
      >
        {value ? (
          <>
            <span className="text-teal-600">{renderIcon(value)}</span>
            <span className="truncate">{value}</span>
          </>
        ) : (
          <span>{placeholder}</span>
        )}
      </button>

      {isOpen && (
        <IconPicker
          value={value}
          onChange={onChange}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

// Helper component to render a Phosphor icon by name
interface PhosphorIconProps {
  name: string | null;
  size?: number;
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  className?: string;
}

export function PhosphorIcon({ name, size = 24, weight = 'regular', className }: PhosphorIconProps) {
  if (!name) return null;

  const IconComponent = (PhosphorIcons as unknown as Record<string, React.ComponentType<{ size?: number; weight?: string; className?: string }>>)[name];
  if (!IconComponent) {
    // Fallback: if it looks like an emoji, render it as text
    if (name.length <= 2 || /\p{Emoji}/u.test(name)) {
      return <span className={className} style={{ fontSize: size }}>{name}</span>;
    }
    return null;
  }

  return <IconComponent size={size} weight={weight} className={className} />;
}
